import React, { useEffect, useState, useRef } from "react";
import { getImageUrl, getProfileImageUrlByAccountTypeCode } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { updateCommentLike, removeCommentById, addComment, findCommentById } from "helpers/Comment.helper";
import { IDiary, IDiaryComment } from "api/interfaces/profile.interface";
import { getDiaryDetail, toggleDiaryLike, toggleDiaryBookmark, getDiaryComments, createDiaryComment, toggleCommentLike, deleteDiaryComment, translateDiary, ITranslateResponse, translateDiaryComment, ICommentTranslateResponse } from "api/agency/v2/diary.api";
import VideoPlayer from "commons/video/VideoPlayer";
import { useSelector } from "react-redux";
import ReportPopup from "commons/popup/ReportPopup";
import { getCurrentLangName } from "helpers/String.helper";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import CommentItem from "../../../common/v2/CommentItem";
import { usePopup } from "contexts/PopupContext";
import StudentInfoPopup from "../../../student/v2/popup/StudentInfoPopup";
import KoachInfoPopup from "../../../student/v2/popup/KoachInfoPopup";
import LikePopupForDiary from "./LikePopupForDiary";
import ImageSlidePopup from "pages/common/v2/ImageSlidePopup";
import StudentProfilePopup from "./StudentProfilePopup";

interface StudentDiaryDetailPopupProps {
    diaryId: string;
    onClose: () => void;
}

const StudentDiaryDetailPopup: React.FC<StudentDiaryDetailPopupProps> = ({ diaryId, onClose }) => {
    const { showPopup } = usePopup();
    const [diary, setDiary] = useState<IDiary | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [commentText, setCommentText] = useState("");
    const [comments, setComments] = useState<IDiaryComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [commentsPagination, setCommentsPagination] = useState({ currentPage: 1, hasNextPage: false });
    const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
    const [activeCommentKebabMenu, setActiveCommentKebabMenu] = useState<number | null>(null);
    const commentKebabRef = useRef<HTMLDivElement>(null);
    const [showReportPopup, setShowReportPopup] = useState(false);
    const [commentToReport, setCommentToReport] = useState<{ id: number; content: string } | null>(null);

    // 대댓글 관련 상태
    const [replyToComment, setReplyToComment] = useState<number | null>(null);
    const [replyText, setReplyText] = useState("");
    const [commentLikeLoading, setCommentLikeLoading] = useState<{ [key: number]: boolean }>({});

    // 번역 관련 상태
    const [isTranslating, setIsTranslating] = useState(false);
    const [translatedData, setTranslatedData] = useState<ITranslateResponse | null>(null);
    
    // 북마크 토글 로딩 상태
    const [isBookmarking, setIsBookmarking] = useState(false);
    
    // 좋아요 토글 로딩 상태
    const [isLiking, setIsLiking] = useState(false);
    
    // 코멘트 번역 관련 상태
    const [translatingCommentIds, setTranslatingCommentIds] = useState<Set<number>>(new Set());
    const [translatedComments, setTranslatedComments] = useState<Map<number, ICommentTranslateResponse>>(new Map());

    // 좋아요 팝업 표시 함수
    const handleShowLikePopup = () => {
        if (diary && diary.likeCount && diary.likeCount > 0) {
            const popupContent = (
                <LikePopupForDiary
                    diaryId={diaryId}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        }
    };

    // Redux store에서 사용자 정보 가져오기
    const userInfo = useSelector((state: any) => state.user.info);

    useEffect(() => {
        fetchDiaryData();
    }, [diaryId]);

    // 팝업 내부 스크롤 이벤트 감지하여 코멘트 무한 스크롤
    useEffect(() => {
        let isScrolling = false; // 스크롤 중복 방지 플래그

        const handleScroll = (event: Event) => {
            const target = event.target as HTMLElement;
            if (!target) return;

            // 이미 스크롤 처리 중이면 무시
            if (isScrolling) return;

            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;

            // 스크롤이 하단 근처에 도달했는지 확인 (100px 여유)
            if (scrollTop + clientHeight >= scrollHeight - 100) {
                if (!isLoadingMoreComments && commentsPagination.hasNextPage) {
                    console.log('Loading next page...');
                    isScrolling = true; // 스크롤 처리 시작

                    const nextPage = commentsPagination.currentPage + 1;
                    fetchCommentsData(nextPage, true).finally(() => {
                        // API 호출 완료 후 플래그 리셋 (1초 후)
                        setTimeout(() => {
                            isScrolling = false;
                        }, 1000);
                    });
                } else {
                    console.log('Cannot load next page:', {
                        isLoadingMore: isLoadingMoreComments,
                        hasNextPage: commentsPagination.hasNextPage
                    });
                }
            }
        };

        // 팝업의 스크롤 가능한 요소를 찾아서 이벤트 리스너 추가
        const popupElement = document.querySelector('.overflow-y-auto');
        if (popupElement) {
            console.log('Scroll event listener added to:', popupElement);
            popupElement.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                popupElement.removeEventListener('scroll', handleScroll);
            };
        } else {
            console.log('Popup element with .overflow-y-auto not found');
        }
    }, [commentsPagination.hasNextPage, isLoadingMoreComments, commentsPagination.currentPage]); // 필요한 의존성 추가

    const fetchDiaryData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await getDiaryDetail(diaryId);
            setDiary(result.diary);

            // 다이어리 상세 정보를 성공적으로 가져온 후 코멘트 목록 호출
            await fetchCommentsData();
        } catch (error) {
            console.error("다이어리 상세 조회 실패:", error);
            setError("다이어리를 불러오는데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCommentsData = async (page: number = 1, append: boolean = false) => {
        console.log('fetchCommentsData called:', { page, append });

        try {
            if (append) {
                setIsLoadingMoreComments(true);
            } else {
                setCommentsLoading(true);
            }

            const result = await getDiaryComments(diaryId, { page });
            console.log('API response:', result);

            if (append && comments.length > 0) {
                // 기존 코멘트에 새 코멘트 추가
                setComments(prevComments => [...prevComments, ...result.commentList]);
            } else {
                // 첫 페이지 로드
                setComments(result.commentList);
            }

            // 페이지네이션 정보 업데이트
            setCommentsPagination(prevPagination => {
                const newPagination = {
                    currentPage: result.pagination.currentPage,
                    hasNextPage: result.pagination.hasNextPage
                };
                console.log('Pagination updated:', newPagination);
                return newPagination;
            });

        } catch (error) {
            console.error("코멘트 목록 조회 실패:", error);
            if (!append) {
                setComments([]);
            }
        } finally {
            if (append) {
                setIsLoadingMoreComments(false);
            } else {
                setCommentsLoading(false);
            }
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleImageClick = (imageUrl: string, imageIndex: number = 0) => {
        // 이미지 클릭 시 ImageSlidePopup 표시
        if (diary && diary.diaryImages && diary.diaryImages.length > 0) {
            const popupContent = (
                <ImageSlidePopup
                    imageInfoList={diary.diaryImages.map(diaryImage => diaryImage.image)}
                    initialSlideIdx={imageIndex}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        }
    };

    const handleVideoClick = (videoUrl: string) => {
        // 비디오 클릭 시 새 창에서 재생 (필요시 구현)
        window.open(videoUrl, '_blank');
    };

    const handleLikeToggle = async () => {
        // 이미 좋아요 처리 중인지 확인
        if (isLiking) {
            console.log("🚫 이미 좋아요 처리 중인 다이어리:", diaryId);
            return;
        }

        try {
            // 좋아요 처리 시작
            setIsLiking(true);
            console.log("❤️ 좋아요 토글 시작:", diaryId);

            const result = await toggleDiaryLike(diaryId);
            console.log("좋아요 토글 결과:", result);

            // 다이어리 데이터 업데이트
            setDiary(prevDiary => {
                if (!prevDiary) return prevDiary;
                return {
                    ...prevDiary,
                    isLiked: result.isLiked,
                    likeCount: result.likeCount
                };
            });

        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 좋아요 처리 완료
            setIsLiking(false);
            console.log("✅ 좋아요 토글 완료:", diaryId);
        }
    };

    const handleBookmarkToggle = async () => {
        // 이미 북마크 처리 중인지 확인
        if (isBookmarking) {
            console.log("🚫 이미 북마크 처리 중인 다이어리:", diaryId);
            return;
        }

        try {
            // 북마크 처리 시작
            setIsBookmarking(true);
            console.log("🔖 북마크 토글 시작:", diaryId);

            const result = await toggleDiaryBookmark(diaryId);
            console.log("북마크 토글 결과:", result);

            // 다이어리 데이터 업데이트
            setDiary(prevDiary => {
                if (!prevDiary) return prevDiary;
                return {
                    ...prevDiary,
                    isBookmarked: result.isBookmarked,
                    bookmarkCount: result.bookmarkCount
                };
            });

        } catch (error) {
            console.error("북마크 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 북마크 처리 완료
            setIsBookmarking(false);
            console.log("✅ 북마크 토글 완료:", diaryId);
        }
    };

    // 번역 처리
    const handleTranslate = async () => {
        if (isTranslating || translatedData) {
            console.log("🚫 이미 번역 처리 중이거나 번역된 다이어리:", diaryId);
            return;
        }

        try {
            setIsTranslating(true);
            console.log("🌐 번역 시작:", diaryId);

            const result = await translateDiary(diaryId);
            console.log("번역 결과:", result);

            setTranslatedData(result);

        } catch (error) {
            console.error("번역 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            setIsTranslating(false);
            console.log("✅ 번역 완료:", diaryId);
        }
    };

    // 코멘트 번역 처리
    const handleCommentTranslate = async (commentId: number) => {
        if (translatingCommentIds.has(commentId) || translatedComments.has(commentId)) {
            console.log("🚫 이미 번역 처리 중이거나 번역된 코멘트:", commentId);
            return;
        }

        try {
            setTranslatingCommentIds(prev => new Set(prev).add(commentId));
            console.log("🌐 코멘트 번역 시작:", commentId);

            const result = await translateDiaryComment(diaryId, commentId.toString());
            console.log("코멘트 번역 결과:", result);

            setTranslatedComments(prev => new Map(prev).set(commentId, result));

        } catch (error) {
            console.error("코멘트 번역 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            setTranslatingCommentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(commentId);
                return newSet;
            });
            console.log("✅ 코멘트 번역 완료:", commentId);
        }
    };

    const handleCommentSubmit = async () => {
        // 대댓글 모드인지 확인
        const isReplyMode = replyToComment !== null;
        const textToSubmit = isReplyMode ? replyText : commentText;

        if (!textToSubmit.trim() || isSubmittingComment) return;

        try {
            setIsSubmittingComment(true);

            // 코멘트 작성 API 호출
            await createDiaryComment(diaryId, textToSubmit.trim(), replyToComment || undefined);

            // 입력란 초기화
            setCommentText("");
            setReplyToComment(null);
            setReplyText("");

            // 코멘트 목록 새로고침
            await fetchCommentsData();
        } catch (error) {
            console.error("코멘트 작성 실패:", error);
            // TODO: 에러 메시지 표시 (팝업 또는 토스트)
        } finally {
            setIsSubmittingComment(false);
        }
    };

    // 코멘트 케밥 메뉴 토글
    const toggleCommentKebabMenu = (commentId: number) => {
        setActiveCommentKebabMenu(activeCommentKebabMenu === commentId ? null : commentId);
    };

    // 내 코멘트인지 확인
    const isMyComment = (comment: IDiaryComment) => {
        return comment.userId === userInfo?.id;
    };

    // 코멘트 삭제 처리
    const handleDeleteComment = async (commentId: number) => {
        setActiveCommentKebabMenu(null);

        // 브라우저 confirm 팝업으로 삭제 확인
        const isConfirmed = window.confirm("정말로 이 코멘트를 삭제하시겠습니까?");

        if (!isConfirmed) return;

        try {
            await deleteDiaryComment(diaryId, commentId);
            console.log('코멘트 삭제 성공:', commentId);

            // 헬퍼 함수를 사용하여 부모/자식 댓글에서 해당 commentId 제거
            setComments(prevComments => removeCommentById(prevComments, commentId));

            // 삭제된 코멘트가 마지막 페이지의 마지막 코멘트였다면 이전 페이지로 이동
            if (comments.length === 1 && commentsPagination.currentPage > 1) {
                const prevPage = commentsPagination.currentPage - 1;
                await fetchCommentsData(prevPage, false);
            }
        } catch (error) {
            console.error('코멘트 삭제 실패:', error);
            // TODO: 에러 메시지 표시
        }
    };

    // 코멘트 신고 팝업 표시
    const handleReportComment = (commentId: number, commentContent: string) => {
        setCommentToReport({ id: commentId, content: commentContent });
        setShowReportPopup(true);
        setActiveCommentKebabMenu(null);
    };

    // 케밥 메뉴 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (commentKebabRef.current && !commentKebabRef.current.contains(event.target as Node)) {
                setActiveCommentKebabMenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCommentLikeToggle = async (commentId: number) => {
        try {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: true }));
            console.log("❤️ 댓글 좋아요 토글 시작:", commentId);
            const result = await toggleCommentLike(diaryId, commentId);
            console.log("✅ 댓글 좋아요 토글 결과:", { commentId, result });

            // 헬퍼 함수를 사용하여 부모/자식 댓글 모두에서 해당 commentId 찾아서 업데이트
            setComments(prevComments =>
                updateCommentLike(prevComments, commentId, {
                    isLiked: result.isLiked,
                    likeCount: result.likeCount
                })
            );
        } catch (error) {
            console.error("❌ 댓글 좋아요 토글 실패:", error);
            // TODO: 에러 메시지 표시 (팝업 또는 토스트)
        } finally {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: false }));
        }
    };

    // 대댓글 클릭 핸들러
    const handleReplyClick = (commentId: number) => {
        if (replyToComment === commentId) {
            // 이미 대댓글 모드인 경우 취소
            setReplyToComment(null);
            setReplyText("");
        } else {
            // 새로운 대댓글 모드 시작
            setReplyToComment(commentId);
            setReplyText("");
        }
    };

    // 대댓글 취소 핸들러
    const handleCancelReply = () => {
        setReplyToComment(null);
        setReplyText("");
    };

    // 통일된 사용자 클릭 핸들러
    const handleUserClick = (userId: string, userType: string) => {
        if (userType === 'student') {
            const popupContent = (
                <StudentProfilePopup
                    userStudentId={parseInt(userId)}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        } else if (userType === 'koach') {
            const popupContent = (
                <KoachInfoPopup
                    koachId={userId}
                    onClose={() => showPopup(null)}
                />
            );
            showPopup(popupContent);
        }
    };

    // 로딩 상태
    if (isLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="animate-pulse p-6">
                        {/* 헤더 스켈레톤 */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                                <div>
                                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                                </div>
                            </div>
                            <div className="w-6 h-6 bg-gray-200 rounded"></div>
                        </div>

                        {/* 제목 스켈레톤 */}
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>

                        {/* 내용 스켈레톤 */}
                        <div className="space-y-3 mb-6">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                        </div>

                        {/* 미디어 스켈레톤 */}
                        <div className="space-y-4">
                            {/* 비디오 스켈레톤 */}
                            <div>
                                <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
                                <div className="h-48 bg-gray-200 rounded-lg"></div>
                            </div>

                            {/* 이미지 스켈레톤 */}
                            <div>
                                <div className="h-4 bg-gray-200 rounded w-16 mb-3"></div>
                                <div className="flex gap-3">
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                    <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                                </div>
                            </div>
                        </div>

                        {/* 통계 정보 스켈레톤 */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-6">
                            <div className="flex items-center space-x-4">
                                <div className="h-4 bg-gray-200 rounded w-8"></div>
                                <div className="h-4 bg-gray-200 rounded w-8"></div>
                            </div>
                            <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error || !diary) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 p-6">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            다이어리 로드 실패
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {error || "다이어리를 불러올 수 없습니다."}
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            닫기
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="popup-content bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* 헤더 - 스크롤해도 항상 상단에 고정 */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between py-2 px-4">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full overflow-hidden">
                                <img
                                    src={getImageUrl(diary.user.userStudent.profileImage) || "/imgs/dummy/basic-img.png"}
                                    alt={diary.user.userStudent.nickname}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">
                                    {diary.user.userStudent.nickname}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {formatTimeAgo(new Date(diary.createdAt))}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* 번역 버튼 */}
                            <button
                                className={`flex items-center space-x-1 px-2 py-1 rounded-lg transition-all duration-200 ${
                                    isTranslating
                                        ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                        : translatedData
                                        ? 'bg-[#CF3688]/10 text-[#CF3688] hover:bg-[#CF3688]/20'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                                onClick={handleTranslate}
                                disabled={isTranslating}
                            >
                                {isTranslating ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <img 
                                        src={translatedData ? "/imgs/icons/icon-translate-active.svg" : "/imgs/icons/icon-translate.svg"} 
                                        alt="translate" 
                                        className="w-4 h-4" 
                                    />
                                )}
                                <span className="text-xs font-medium">
                                    번역
                                </span>
                            </button>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 다이어리 내용 */}
                <div className="p-6">
                    {/* 제목 */}
                    <div className="mb-4">
                        <div className="flex items-start justify-between">
                            <h2 
                                className="text-xl font-bold text-gray-900 flex-1 pr-4"
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal'
                                }}
                            >
                                {diary.title}
                            </h2>
                            {/* 북마크 버튼 */}
                            <button
                                className={`flex flex-col items-center space-y-1 transition-opacity flex-shrink-0 ${isBookmarking
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:opacity-80'
                                    }`}
                                onClick={handleBookmarkToggle}
                                disabled={isBookmarking}
                            >
                                {isBookmarking ? (
                                    // 로딩 중일 때 스피너 표시
                                    <svg className="w-6 h-6 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    // 북마크 아이콘
                                    <img 
                                        src={`/imgs/icons/icon-bookmark${diary.isBookmarked ? '-active' : ''}.svg`} 
                                        alt="bookmark" 
                                        className="w-6 h-6" 
                                    />
                                )}
                                <span className={`text-xs ${diary.isBookmarked ? 'text-[#CF3688]' : 'text-gray-500'}`}>
                                    {diary.bookmarkCount || 0}
                                </span>
                            </button>
                        </div>
                        {translatedData && (
                            <h2 
                                className="text-xl font-bold text-gray-500 mt-2 pl-3 border-l-2 border-gray-400"
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal'
                                }}
                            >
                                {translatedData.translatedTitle}
                            </h2>
                        )}
                    </div>

                    {/* 내용 */}
                    <div className="mb-6">
                        <div 
                            className="text-gray-700 whitespace-pre-wrap"
                            style={{
                                wordBreak: 'break-all',
                                overflowWrap: 'anywhere',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {diary.content}
                        </div>
                        {translatedData && (
                            <div 
                                className="text-gray-500 mt-2 pl-3 border-l-2 border-gray-400"
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'pre-wrap'
                                }}
                            >
                                {translatedData.translatedContent}
                            </div>
                        )}
                    </div>

                    {/* 비디오들 (우선 출력) */}
                    {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                        <div className="mb-6">
                            <div className="space-y-3">
                                {diary.diaryVideos.map((diaryVideo) => (
                                    <VideoPlayer
                                        key={diaryVideo.id}
                                        video={diaryVideo.video}
                                        className="w-full h-full object-cover"
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 이미지들 (좌우 스크롤) */}
                    {diary.diaryImages && diary.diaryImages.length > 0 && (
                        <div className="mb-6">
                            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                {diary.diaryImages.map((diaryImage, index) => (
                                    <div
                                        key={diaryImage.id}
                                        className="flex-shrink-0 aspect-square w-24 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => handleImageClick(getImageUrl(diaryImage.image), index)}
                                    >
                                        <img
                                            src={getImageUrl(diaryImage.image)}
                                            alt={`다이어리 이미지 ${diaryImage.sequence}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 통계 정보 */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                            {/* 좋아요 버튼 */}
                            <button
                                className={`flex items-center space-x-1 transition-opacity ${isLiking
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:opacity-80'
                                    }`}
                                onClick={handleLikeToggle}
                                disabled={isLiking}
                            >
                                {isLiking ? (
                                    // 로딩 중일 때 스피너 표시
                                    <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    // 좋아요 아이콘
                                    <img src={`/imgs/icons/icon-heart${diary.isLiked ? '-active' : ''}.svg`} alt="like" className="w-4 h-4" />
                                )}
                                <span className={diary.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}>
                                    {diary.likeCount || 0}
                                </span>
                            </button>

                            {/* 댓글 수 */}
                            {diary.commentCount !== undefined && (
                                <div className="flex items-center space-x-1">
                                    <img src="/imgs/icons/icon-comment.svg" alt="comment" className="w-4 h-4" />
                                    <span>{diary.commentCount}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 활동영역 - 홈화면과 동일한 구조 */}
                    {diary.likeCount && diary.likeCount > 0 && (
                        <div className="pt-4 border-t border-gray-100 mb-6">
                            <div className="flex items-center justify-between">
                                {/* 좋아요한 유저들의 프로필 사진 */}
                                <div className="flex -space-x-2">
                                    {diary.likes && diary.likes.slice(0, 5).map((like, index) => (
                                        <div
                                            key={like.userId}
                                            className="w-8 h-8 rounded-full border-2 border-white overflow-hidden shadow-sm"
                                            style={{ zIndex: 5 - index }}
                                        >
                                            <img
                                                src={getProfileImageUrlByAccountTypeCode(like?.user, 32, 32) || "/imgs/dummy/basic-img.png"}
                                                alt={like?.user?.userStudent?.nickname || like?.user?.username || "User"}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = "/imgs/dummy/basic-img.png";
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* 좋아요 텍스트 - 우선순위별 조건 */}
                                {diary.castingManagerLikeCount !== undefined && diary.castingManagerLikeCount > 0 ? (
                                    /* 1순위: 캐스팅 매니저 좋아요가 있는 경우 */
                                    <div 
                                        className="flex items-center space-x-1 text-xs text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleShowLikePopup();
                                        }}
                                    >
                                        <span className="text-gray-500">
                                            {diary.castingManagerLikeCount === 1 
                                                ? getLanguageValue(PAGE_TYPE.AGENCY, "diaryDetail.txtCastingManagerLikeSingle")
                                                : getLanguageValue(PAGE_TYPE.AGENCY, "diaryDetail.txtCastingManagerLikeMultiple")
                                            }
                                        </span>
                                    </div>
                                ) : (
                                    /* 2순위: 캐스팅 매니저 좋아요가 없고, 일반 좋아요가 3개 초과인 경우 */
                                    diary.likeCount > 3 && (
                                        <div 
                                            className="flex items-center space-x-1 text-xs text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowLikePopup();
                                            }}
                                        >
                                            <span className="text-gray-500">
                                                {getLanguageValue(PAGE_TYPE.AGENCY, "diaryDetail.txtOthersLike")}
                                            </span>
                                        </div>
                                    )
                                )}

                                {/* 화살표 아이콘 */}
                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </div>
                    )}

                    {/* 코멘트 섹션 */}
                    <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            코멘트
                        </h3>

                        {/* 코멘트 입력 영역은 팝업 하단에 고정 */}

                        {/* 코멘트 목록 */}
                        <div className="space-y-1.5">
                            {commentsLoading ? (
                                // 코멘트 스켈레톤 UI
                                <>
                                    {[1, 2, 3].map((index) => (
                                        <div key={`skeleton-${index}`} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                                            {/* 프로필 이미지 스켈레톤 */}
                                            <div className="flex items-center space-x-2 mb-1.5">
                                                <div className="w-7 h-7 bg-gray-300 rounded-full"></div>
                                                <div className="flex flex-col space-y-1.5">
                                                    <div className="h-4 bg-gray-300 rounded w-24"></div>
                                                    <div className="h-3 bg-gray-300 rounded w-16"></div>
                                                </div>
                                            </div>

                                            {/* 코멘트 내용 스켈레톤 */}
                                            <div className="space-y-2 mb-2">
                                                <div className="h-4 bg-gray-300 rounded w-full"></div>
                                                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                            </div>

                                            {/* 좋아요 버튼 스켈레톤 */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-4 h-4 bg-gray-300 rounded"></div>
                                                    <div className="h-3 bg-gray-300 rounded w-8"></div>
                                                </div>
                                                <div className="h-3 bg-gray-300 rounded w-16"></div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            ) : comments.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    아직 작성된 코멘트가 없습니다.
                                </div>
                            ) : (
                                comments.map((comment) => (
                                    <div key={comment.id} className="space-y-2">
                                        {/* 메인 댓글 */}
                                        <CommentItem
                                            comment={comment}
                                            isChild={false}
                                            onLikeToggle={handleCommentLikeToggle}
                                            onReplyClick={handleReplyClick}
                                            onDeleteClick={handleDeleteComment}
                                            onReportClick={handleReportComment}
                                            isMyComment={isMyComment}
                                            commentLikeLoading={commentLikeLoading}
                                            showKebabMenu={activeCommentKebabMenu}
                                            onKebabToggle={toggleCommentKebabMenu}
                                            kebabRef={commentKebabRef}
                                            onUserClick={handleUserClick}
                                            isTranslating={translatingCommentIds.has(comment.id)}
                                            translatedContent={translatedComments.get(comment.id)?.translatedContents}
                                            onTranslate={handleCommentTranslate}
                                        />

                                        {/* 자식 코멘트들 */}
                                        {comment.childList && comment.childList.length > 0 && (
                                            comment.childList.map((childComment) => (
                                                <CommentItem
                                                    key={childComment.id}
                                                    comment={childComment}
                                                    isChild={true}
                                                    onLikeToggle={handleCommentLikeToggle}
                                                    onReplyClick={handleReplyClick}
                                                    onDeleteClick={handleDeleteComment}
                                                    onReportClick={handleReportComment}
                                                    isMyComment={isMyComment}
                                                    commentLikeLoading={commentLikeLoading}
                                                    showKebabMenu={activeCommentKebabMenu}
                                                    onKebabToggle={toggleCommentKebabMenu}
                                                    kebabRef={commentKebabRef}
                                                    onUserClick={handleUserClick}
                                                    isTranslating={translatingCommentIds.has(childComment.id)}
                                                    translatedContent={translatedComments.get(childComment.id)?.translatedContents}
                                                    onTranslate={handleCommentTranslate}
                                                />
                                            ))
                                        )}
                                    </div>
                                ))
                            )}

                            {/* 추가 코멘트 로딩 상태 */}
                            {isLoadingMoreComments && (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                    <span className="text-gray-500 text-sm">더 많은 코멘트를 불러오는 중...</span>
                                </div>
                            )}

                            {/* 더 이상 로드할 코멘트가 없을 때 */}
                            {!isLoadingMoreComments && !commentsPagination.hasNextPage && comments.length > 0 && (
                                <div className="flex justify-center items-center py-8">
                                    <div className="text-center">
                                        <div className="w-8 h-8 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 고정된 코멘트 입력란을 위한 하단 여백 */}
                <div className="h-24"></div>
            </div>

            {/* 코멘트 신고 팝업 */}
            {showReportPopup && commentToReport && (
                <ReportPopup
                    type="comment"
                    diaryId={diaryId}
                    commentId={commentToReport.id.toString()}
                    content={commentToReport.content}
                    onClose={() => {
                        setShowReportPopup(false);
                        setCommentToReport(null);
                    }}
                    onSuccess={() => {
                        // 신고 성공 시 처리 (필요시 추가)
                        console.log('코멘트 신고가 완료되었습니다.');
                    }}
                />
            )}

            {/* 스티키 코멘트 입력란 - 팝업 하단에 고정 */}
            {/* 
            <div className="fixed bottom-4 left-4 right-4 z-10">
                <div className="backdrop-blur-md bg-white/80 rounded-2xl shadow-lg border border-gray-200/50 p-4">
                    {/* 대댓글 표시 영역 */}
                    {/* 
                    {replyToComment && (
                        <div className="mb-2 p-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {/* 대댓글 아이콘 */}
                                    {/* 
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                    </svg>

                                    {/* 댓글 내용 */}
                                    {/* 
                                    <span className="text-sm text-gray-600 truncate max-w-[200px]">
                                        {comments.find(c => c.id === replyToComment)?.contents || ""}
                                    </span>
                                </div>

                                {/* 삭제 버튼 */}
                                {/* 
                                <button
                                    onClick={handleCancelReply}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 댓글 입력 폼 */}
                    {/* 
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={replyToComment ? replyText : commentText}
                            onChange={(e) => replyToComment ? setReplyText(e.target.value) : setCommentText(e.target.value)}
                            placeholder={replyToComment
                                ? "대댓글을 입력하세요..."
                                : "지망생에게 힘이되는 한마디를 입력해주세요 :)"
                            }
                            maxLength={200}
                            disabled={isSubmittingComment}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-[#CF3688] text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            onKeyPress={(e) => e.key === 'Enter' && !isSubmittingComment && handleCommentSubmit()}
                        />
                        <button
                            onClick={handleCommentSubmit}
                            disabled={replyToComment ? !replyText.trim() : !commentText.trim() || isSubmittingComment}
                            className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#CF3688]/90 focus:outline-none focus:ring-2 focus:ring-[#CF3688] disabled:cursor-not-allowed transition-colors text-sm font-medium"
                        >
                            {replyToComment ? "답글" : "입력"}
                        </button>
                    </div>
                </div>
            </div>
            */}
        </div>
    );
};

export default StudentDiaryDetailPopup;
